import axios from 'axios';
import { KeyValuePair } from 'helpers';
import { TicketQueue, UserType, TicketFilters, TicketColumns } from 'helpers/enums';
import { ItemListResponse, ITicketSearchRequest, SaveResponse } from 'helpers/interfaces';
import { appUserContext } from 'helpers/services';
import { Dictionary } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router';

interface IContext {
    recordCount: number
    selectAllPages: boolean
    queue: TicketQueue | ''
    defaultSearch: ITicketSearchRequest
    search: ITicketSearchRequest
    tickets: ITicket[]
    isLoading: boolean
    ticketQueuesCount: KeyValuePair<TicketQueue, number>[]
    supplier: string
    filtersByQueue: KeyValuePair<string, string[]>[]
    filterLabel: Dictionary<string>
    lastExecutedSearch: ITicketSearchRequest
    selectedItems: string[]

    setSelectedItems(v: string[]): void
    selectAll: () => void
    saveSelectedFilters: (v: TicketFilters[]) => void
    updateSortColum: (v: TicketColumns) => void
    setLoading: (v: boolean) => void
    updateSupplier: (v: string) => void
    setSupplier: (value: string) => void
    updateSearch: (newSearch: ITicketSearchRequest) => void
    updateQueue: (queue: TicketQueue | '') => void
    doSearch: (newSearch: ITicketSearchRequest) => void,
    changePage: (page: number) => void
    deleteTicket: (ticketNumber: string) => Promise<void>
}

interface ITicket {
    ticketNumber: string
    id: string
    title: string
    createdOn: string
    assignee: string
    customerServiceAssignee: string
    groups: string[]
    ticketStatusId: string
    ticketPriorityId: string
    reporterName: string
    reporterCompany: string
    reporters: string
    ticketTypeId: string
}

export interface IState {
    search: ITicketSearchRequest
    queue: TicketQueue | ''
    supplier: string
}

export let ticketsContext = React.createContext({} as IContext);

let { Provider } = ticketsContext;

let TicketsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    let { currentUser } = useContext(appUserContext);

    let location = useLocation<IState>();
    let history = useHistory<IState>();
    let { enqueueSnackbar } = useSnackbar();

    let allFilters = [
        TicketFilters.Ticket,
        TicketFilters.Status,
        TicketFilters.Type,
        TicketFilters.LastUpdateFrom,
        TicketFilters.Supplier,
        TicketFilters.Reporter,
        TicketFilters.Group,
        TicketFilters.Assignee,
        TicketFilters.CSAssignee,
        TicketFilters.ServiceOrder,
        TicketFilters.CustomerOrder,
        TicketFilters.ProductSKU,
        TicketFilters.Customer,
        TicketFilters.SerialNumber
    ]

    let filterLabel: Dictionary<string> = {
        [TicketFilters.Ticket]: "Ticket",
        [TicketFilters.Status]: "Status",
        [TicketFilters.Type]: "Type",
        [TicketFilters.LastUpdateFrom]: "Last Update From",
        [TicketFilters.Supplier]: "Supplier",
        [TicketFilters.Group]: "Group",
        [TicketFilters.Reporter]: "Reporter",
        [TicketFilters.Assignee]: "Assignee",
        [TicketFilters.CSAssignee]: "C.S. Assignee",
        [TicketFilters.ServiceOrder]: "Service order nr.",
        [TicketFilters.CustomerOrder]: "Customer order nr.",
        [TicketFilters.ProductSKU]: "Product SKU",
        [TicketFilters.Customer]: "Customer",
        [TicketFilters.SerialNumber]: "Serial Nr."
    }

    let filtersByQueue = [
        new KeyValuePair<string, string[]>('', allFilters),
        new KeyValuePair<string, string[]>(TicketQueue.NewUnassigned, [
            TicketFilters.Ticket,
            TicketFilters.Status,
            TicketFilters.Type,
            TicketFilters.LastUpdateFrom,
            TicketFilters.Supplier,
            TicketFilters.Reporter,
            TicketFilters.Group
        ]),
        new KeyValuePair<string, string[]>(TicketQueue.QualityUnassigned, [
            TicketFilters.Ticket,
            TicketFilters.Status,
            TicketFilters.Type,
            TicketFilters.LastUpdateFrom,
            TicketFilters.Supplier,
            TicketFilters.Reporter,
            TicketFilters.CSAssignee
        ]),
        new KeyValuePair<string, string[]>(TicketQueue.LogisticUnassigned, [
            TicketFilters.Ticket,
            TicketFilters.Status,
            TicketFilters.Type,
            TicketFilters.LastUpdateFrom,
            TicketFilters.Supplier,
            TicketFilters.Reporter,
            TicketFilters.CSAssignee
        ]),
        new KeyValuePair<string, string[]>(TicketQueue.MyOpenTickets, allFilters),
        new KeyValuePair<string, string[]>(TicketQueue.WaitingForVidendum, allFilters),
        new KeyValuePair<string, string[]>(TicketQueue.WaitingForReporter, allFilters)
    ]

    let defaultSearch: ITicketSearchRequest = {
        order: {
            column: TicketColumns.Ticket,
            descending: true
        },
        userGroup: "",
        customerStatus: [],
        supplier: "",
        pageNr: 0,
        pageSize: 50,
        fullText: "",
        status: [],
        type: "",
        assignee: '',
        csAssignee: '',
        reporter: '',
        reporterName: '',
        lastUpdatedOnFrom: null,
        customerOrder: '',
        productSKU: '',
        customerM3Id: '',
        customerName: '',
        serviceOrder: '',
        serialNumber: ''
    }

    let [lastExecutedSearch, setLastExecutedSearch] = useState(defaultSearch);
    let [isLoading, setLoading] = useState(true);
    let [selectAllPages, setSelectAllPages] = useState(false);
    let [selectedItems, setSelectedItems] = useState<string[]>([])
    let [search, setSearch] = useState<ITicketSearchRequest>(defaultSearch);

    let [queue, setQueue] = useState<TicketQueue | ''>(currentUser?.typology === UserType.VitecUser ? TicketQueue.TicketsDashboard : '');

    let [tickets, setTickets] = useState<ITicket[]>([]);
    let [recordCount, setRecordCount] = useState<number>(0);
    let [ticketQueuesCount, setTicketQueuesCount] = useState<KeyValuePair<TicketQueue, number>[]>([]);
    let [supplier, setSupplier] = useState("");


    useEffect(() => {
        let getTicketQueuesCount = async () => {
            try {
                let res = await axios.get<ItemListResponse<KeyValuePair<TicketQueue, number>>>('/api/Ticket/GetTicketQueuesCount');
                setTicketQueuesCount(res.data.items);
            } catch (err) { }
        }

        getTicketQueuesCount();
    }, [])

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search, queue, supplier } }} />


    let processState = (state: IState) => {
        let request = { ...search }

        if (state.search) {
            request = {
                ...request,
                ...state.search
            }
        }

        setQueue(state.queue);
        setSupplier(state.supplier);
        setSearch(request);

        if (state.queue !== TicketQueue.TicketsDashboard)
            searchTickets(request, state.queue);
        else
            setLoading(false)

    }

    let doSearch = (newSearch: ITicketSearchRequest) => {
        let request = {
            ...newSearch,
            pageNr: newSearch.pageNr > 0 ? 0 : newSearch.pageNr
        }

        history.push(location.pathname, { search: request, queue, supplier: currentUser?.defaultSupplier || '' });
    }

    let updateSearch = (newSearch: ITicketSearchRequest) => {
        setSearch(newSearch);
    }

    let updateQueue = (queue: TicketQueue | '') => {
        let request = {
            ...search,
            ...defaultSearch,
        } as ITicketSearchRequest

        history.push(location.pathname, { search: request, queue: queue, supplier: currentUser?.defaultSupplier || '' })
    }

    let updateSupplier = (supplier: string) => {
        let request = {
            ...search,
            ...defaultSearch,
        } as ITicketSearchRequest

        history.push(location.pathname, { search: request, queue, supplier })
    }



    let saveSelectedFilters = async (v: TicketFilters[]) => {
        try {
            await axios.post<SaveResponse>('/api/User/SaveUserFilters', v);
        } catch (error) { }
    }

    let searchTickets = async (searchRequest: ITicketSearchRequest, queue: TicketQueue | '') => {
        try {
            setLoading(true);

            setSelectedItems([]);
            setSelectAllPages(false);

            let request = {
                ...searchRequest,
                queue
            }

            let response = await axios.post<ItemListResponse<ITicket>>('/api/Ticket/GetTickets', request);
            setTickets(response.data.items);
            setRecordCount(response.data.recordCount);
            setLastExecutedSearch(searchRequest);


        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let changePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request, queue, supplier: currentUser?.defaultSupplier || '' });
    }

    let deleteTicket = async (ticketNumber: string) => {
        if (window.confirm("Are you sure that you want to delete this ticket?") === false)
            return;

        try {
            await axios.delete(`/api/Ticket/DeleteTicket/${ticketNumber}`);
            setTickets([...tickets.filter(x => x.ticketNumber !== ticketNumber)]);
            setRecordCount(recordCount - 1);

            setSelectedItems([]);
            setSelectAllPages(false);

            enqueueSnackbar("Ticket deleted", { variant: "success" });
        } catch (err) { }
    }

    let updateSortColum = (column: TicketColumns) => {
        let request = {
            ...search,
            order: {
                column,
                descending: search.order?.column === column ? !search.order.descending : true
            }
        };

        doSearch(request);
    }

    let selectAll = () => {
        if (selectAllPages === true) {
            setSelectedItems([]);
        } else {
            setSelectedItems(tickets.map(x => x.id))
        }

        setSelectAllPages(!selectAllPages);
    }


    return (
        <Provider
            value={{
                selectAllPages,
                isLoading,
                queue,
                tickets,
                defaultSearch,
                search,
                recordCount,
                ticketQueuesCount,
                supplier,
                filtersByQueue,
                filterLabel,
                lastExecutedSearch,
                selectedItems,

                setSelectedItems,
                selectAll,
                saveSelectedFilters,
                setLoading,
                updateSortColum,
                updateSupplier,
                setSupplier,
                updateSearch,
                updateQueue,
                doSearch,
                changePage,
                deleteTicket
            }}
        >
            {children}
        </Provider>
    )
}

export default TicketsProvider;